/**
 * Platform-aware storage abstraction layer
 * Uses localStorage on web, Capacitor Preferences on native
 */

// Dynamically check if we're on a native platform
const checkIsNative = (): boolean => {
  try {
    // Check if Capacitor is available and we're on native
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const capacitor = (window as any).Capacitor;
    return capacitor?.isNativePlatform?.() ?? false;
  } catch {
    return false;
  }
};

interface StorageInterface {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

// Lazy-load Capacitor Preferences only when needed on native
const getNativeStorage = async () => {
  const { Preferences } = await import('@capacitor/preferences');
  return Preferences;
};

// Native storage using Capacitor Preferences
const createNativeStorage = (): StorageInterface => ({
  async get(key: string): Promise<string | null> {
    const Preferences = await getNativeStorage();
    const { value } = await Preferences.get({ key });
    return value;
  },
  async set(key: string, value: string): Promise<void> {
    const Preferences = await getNativeStorage();
    await Preferences.set({ key, value });
  },
  async remove(key: string): Promise<void> {
    const Preferences = await getNativeStorage();
    await Preferences.remove({ key });
  },
});

// Web storage using localStorage
const webStorage: StorageInterface = {
  async get(key: string): Promise<string | null> {
    return localStorage.getItem(key);
  },
  async set(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
  },
  async remove(key: string): Promise<void> {
    localStorage.removeItem(key);
  },
};

// Cache the native check
let _isNative: boolean | null = null;
const isNative = (): boolean => {
  if (_isNative === null) {
    _isNative = checkIsNative();
  }
  return _isNative;
};

// Export the appropriate storage based on platform
export const storage: StorageInterface = isNative() ? createNativeStorage() : webStorage;

/**
 * Synchronous storage for cases where async isn't practical
 * Falls back to localStorage with sync to native when possible
 */
export const syncStorage = {
  get(key: string): string | null {
    return localStorage.getItem(key);
  },
  set(key: string, value: string): void {
    localStorage.setItem(key, value);
    // Also persist to native storage if on native platform
    if (isNative()) {
      getNativeStorage()
        .then(Preferences => Preferences.set({ key, value }))
        .catch(console.error);
    }
  },
  remove(key: string): void {
    localStorage.removeItem(key);
    if (isNative()) {
      getNativeStorage()
        .then(Preferences => Preferences.remove({ key }))
        .catch(console.error);
    }
  },
};

/**
 * Session storage wrapper (not persisted on native)
 */
export const sessionStore = {
  get(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Session storage might not be available
    }
  },
  remove(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Session storage might not be available
    }
  },
};

/**
 * Capacitor storage adapter for Supabase auth
 * This implements the interface Supabase expects for custom storage
 */
export const supabaseStorageAdapter = {
  getItem: async (key: string): Promise<string | null> => {
    if (isNative()) {
      const Preferences = await getNativeStorage();
      const { value } = await Preferences.get({ key });
      return value;
    }
    return localStorage.getItem(key);
  },
  setItem: async (key: string, value: string): Promise<void> => {
    if (isNative()) {
      const Preferences = await getNativeStorage();
      await Preferences.set({ key, value });
    }
    localStorage.setItem(key, value);
  },
  removeItem: async (key: string): Promise<void> => {
    if (isNative()) {
      const Preferences = await getNativeStorage();
      await Preferences.remove({ key });
    }
    localStorage.removeItem(key);
  },
};
